import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Building2, MessageCircle, Plus, UserPlus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/admin/AdminUI";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ClientAccess } from "@/components/admin/ClientAccess";
import { QuoteOfferDialog, OFFER_LABELS } from "@/components/admin/QuoteOfferDialog";
import { fmt } from "@/components/admin/finance/common";
import { ContactFormDialog } from "@/components/admin/ContactFormDialog";

const STATUSES = ["nouveau", "contacte", "qualifie", "opportunite", "client", "perdu"];
const LABELS = { nouveau: "Nouveau", contacte: "Contacté", qualifie: "Qualifié", opportunite: "Opportunité", client: "Client", perdu: "Perdu" };

export default function CRM() {
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [offerQuote, setOfferQuote] = useState(null);
  const [newQuoteFor, setNewQuoteFor] = useState(null);
  const [contactForm, setContactForm] = useState(null);
  const [tab, setTab] = useState("pipeline");

  const delContact = async (c) => {
    if (!window.confirm(`Supprimer le contact « ${c.nom} » ?`)) return;
    try { await api.delete(`/admin/contacts/${c.id}`); toast.success("Contact supprimé"); setSelected(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delQuote = async (q) => {
    if (!window.confirm(`Supprimer le devis de « ${q.nom} » ?`)) return;
    try { await api.delete(`/admin/quotes/${q.id}`); toast.success("Devis supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = () => {
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    try { await api.put(`/admin/contacts/${id}`, { status }); toast.success("Statut mis à jour"); load(); if (selected?.id === id) setSelected({ ...selected, status }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updateQuote = async (id, status) => {
    try { await api.put(`/admin/quotes/${id}`, { status }); toast.success("Devis mis à jour"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/contacts/${selected.id}/notes`, { text: note }); toast.success("Note ajoutée"); setNote(""); const { data } = await api.get("/admin/contacts"); setContacts(data); setSelected(data.find((c) => c.id === selected.id)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const byStatus = (s) => contacts.filter((c) => c.status === s);

  return (
    <div>
      <PageHeader title="CRM & Devis" subtitle="Suivez vos contacts, prospects, clients et demandes de devis."
        action={<div className="flex flex-wrap gap-2">
          <button onClick={() => setContactForm({})} data-testid="crm-new-contact" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1E293B] text-white text-sm font-semibold hover:bg-[#283548]"><UserPlus className="w-4 h-4" /> Nouveau prospect</button>
          <button onClick={() => setNewQuoteFor("pick")} data-testid="crm-new-quote" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Nouveau devis</button>
        </div>} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="dark bg-[#111827] border border-[#1E293B]">
          <TabsTrigger value="pipeline" data-testid="crm-tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="crm-tab-contacts">Tous les contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="crm-tab-quotes">Demandes de devis ({quotes.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="crm-pipeline">
            {STATUSES.map((s) => (
              <div key={s} className="rounded-xl bg-[#0B0F17] border border-[#1E293B] p-3">
                <div className="flex items-center justify-between mb-3"><span className="text-sm font-semibold text-white">{LABELS[s]}</span><span className="text-xs text-slate-500">{byStatus(s).length}</span></div>
                <div className="space-y-2 min-h-[40px]">
                  {byStatus(s).map((c) => (
                    <button key={c.id} onClick={() => setSelected(c)} className="w-full text-left p-3 rounded-lg bg-[#111827] border border-[#1E293B] hover:border-[#3B82F6]/40">
                      <div className="text-sm font-medium text-white truncate">{c.nom} {c.prenom}</div>
                      <div className="text-xs text-slate-500 truncate">{c.entreprise || c.source}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <Card className="overflow-hidden">
            {contacts.length === 0 ? <EmptyState text="Aucun contact." /> : (
              <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="crm-contacts-table">
                <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>{contacts.map((c) => (
                  <tr key={c.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`crm-contact-row-${c.id}`}>
                    <td className="px-4 py-3 text-white">{c.nom} {c.prenom}</td>
                    <td className="px-4 py-3 text-slate-300">{c.entreprise || "—"}</td>
                    <td className="px-4 py-3 text-slate-400">{c.source}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => setContactForm(c)} data-testid={`crm-contact-edit-${c.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => delContact(c)} data-testid={`crm-contact-delete-${c.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="quotes" className="mt-4">
          <Card className="overflow-hidden">
            {quotes.length === 0 ? <EmptyState text="Aucune demande de devis." /> : (
              <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="crm-quotes-table">
                <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-3">Demandeur</th><th className="px-4 py-3">Besoin</th><th className="px-4 py-3">Budget</th><th className="px-4 py-3">Devis chiffré</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>{quotes.map((q) => (
                  <tr key={q.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50">
                    <td className="px-4 py-3"><div className="text-white">{q.nom}</div><div className="text-xs text-slate-500">{q.entreprise} · {q.email}</div></td>
                    <td className="px-4 py-3 text-slate-300">{q.type_besoin || "—"}<div className="text-xs text-slate-500">{q.solution_service}</div></td>
                    <td className="px-4 py-3 text-slate-400">{q.budget || "—"}</td>
                    <td className="px-4 py-3">
                      {q.offer?.amount > 0 && <div className="text-white whitespace-nowrap">{fmt(q.offer.amount, q.offer.currency)}</div>}
                      {q.offer_status && <div className={`text-xs ${OFFER_LABELS[q.offer_status]?.[1]}`} data-testid={`quote-offer-status-${q.id}`}>{OFFER_LABELS[q.offer_status]?.[0]}{q.invoice_number && ` · ${q.invoice_number}`}</div>}
                      <button onClick={() => setOfferQuote(q)} data-testid={`quote-offer-open-${q.id}`} className="mt-1 text-xs font-semibold text-[#3B82F6] hover:underline">{q.offer_status === "accepte" ? (q.invoice_number ? "Voir" : "Générer la facture") : q.offer_status ? "Modifier" : "Chiffrer"}</button>
                    </td>
                    <td className="px-4 py-3">
                      <select value={q.status} onChange={(e) => updateQuote(q.id, e.target.value)} className="px-2 py-1 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-white text-xs">
                        {STATUSES.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">{!q.invoice_id && <button onClick={() => delQuote(q)} data-testid={`crm-quote-delete-${q.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {offerQuote && <QuoteOfferDialog quote={offerQuote} onClose={() => setOfferQuote(null)} onSaved={load} />}
      {newQuoteFor && <QuoteOfferDialog contact={newQuoteFor === "pick" ? null : newQuoteFor} contacts={newQuoteFor === "pick" ? contacts : null} onClose={() => setNewQuoteFor(null)} onSaved={() => { load(); setTab("quotes"); }} />}
      {contactForm && <ContactFormDialog contact={contactForm.id ? contactForm : null} onClose={() => setContactForm(null)} onSaved={(c) => { load(); if (selected?.id === c.id) setSelected(c); }} />}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B] max-w-lg max-h-[90vh] overflow-y-auto">
          {selected && (<>
            <DialogHeader><DialogTitle className="text-white flex items-center gap-2 pr-6">
              <span className="flex-1 truncate">{selected.nom} {selected.prenom}</span>
              <button onClick={() => setContactForm(selected)} data-testid="contact-detail-edit" className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#1E293B]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => delContact(selected)} data-testid="contact-detail-delete" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-[#1E293B]"><Trash2 className="w-4 h-4" /></button>
            </DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              {selected.entreprise && <div className="flex items-center gap-2 text-slate-300"><Building2 className="w-4 h-4 text-slate-500" />{selected.entreprise} {selected.fonction && `· ${selected.fonction}`}</div>}
              {selected.email && <div className="flex items-center gap-2 text-slate-300"><Mail className="w-4 h-4 text-slate-500" />{selected.email}</div>}
              {selected.telephone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500" />{selected.telephone}</div>}
              {selected.message && <div className="p-3 rounded-lg bg-[#0B0F17] text-slate-300">{selected.message}</div>}
              <ClientAccess contact={selected} />
              <button onClick={() => { setNewQuoteFor(selected); setSelected(null); }} data-testid="contact-new-quote" className="w-full px-4 py-2.5 rounded-xl bg-[#3B82F6]/15 text-[#60A5FA] text-sm font-semibold hover:bg-[#3B82F6]/25">+ Nouveau devis</button>
              <div>
                <label className="text-xs text-slate-400 uppercase font-mono">Statut</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STATUSES.map((s) => <button key={s} onClick={() => updateStatus(selected.id, s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${selected.status === s ? "bg-[#3B82F6] text-white" : "bg-[#0B0F17] text-slate-400 border border-[#1E293B]"}`}>{LABELS[s]}</button>)}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase font-mono">Notes</label>
                <div className="mt-2 space-y-2">
                  {(selected.notes || []).map((n) => <div key={n.id} className="p-2 rounded-lg bg-[#0B0F17] text-slate-300 text-xs"><span className="text-slate-500">{n.author}: </span>{n.text}</div>)}
                </div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-2 bg-[#0B0F17] border-[#1E293B] text-white" placeholder="Ajouter une note…" />
                <button onClick={addNote} className="mt-2 px-4 py-2 rounded-lg bg-[#3B82F6] text-white text-sm font-medium">Ajouter la note</button>
              </div>
              {selected.whatsapp || selected.telephone ? (
                <a href={`https://wa.me/${(selected.whatsapp || selected.telephone).replace(/\D/g, "")}`} target="_blank" rel="noopener" className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] text-white text-sm font-semibold"><MessageCircle className="w-4 h-4" /> Contacter sur WhatsApp</a>
              ) : null}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
